import { useEffect, useState } from "react";
import { App, Button, Form, Input } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { loginWithPassword, probeApiLatencyMs } from "../api/client";
import "../styles/login.css";

type LoginForm = {
  email: string;
  password: string;
};

export function LoginPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [apiDown, setApiDown] = useState(false);

  useEffect(() => {
    let cancelled = false;
    probeApiLatencyMs().then((ms) => {
      if (cancelled) return;
      setLatencyMs(ms);
      setApiDown(ms === null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values: LoginForm) => {
    const email = values.email?.trim();
    if (!email) {
      message.warning("请输入邮箱");
      return;
    }
    setLoading(true);
    try {
      const r = await loginWithPassword(email, values.password);
      if (!r.ok) {
        message.error(r.message);
        return;
      }
      message.success("登录成功");
      navigate("/overview", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <header className="admin-login-topnav">
        <div className="admin-login-topnav-inner">
          <div className="admin-login-topnav-brand">
            <span className="admin-login-logo">XTool</span>
            <span className="admin-login-topnav-divider" />
            <span className="admin-login-topnav-sub">Operations Console</span>
          </div>
          <div className="admin-login-topnav-env">
            <span className="admin-login-term-dot" aria-hidden />
            <span className="admin-login-env-label">Production Environment</span>
          </div>
        </div>
      </header>

      <main className="admin-login-main">
        <div className="admin-login-canvas">
          <div className="admin-login-deco admin-login-deco--tl" aria-hidden />
          <div className="admin-login-card">
            <div className="admin-login-brand-block">
              <div className="admin-login-brand-line">
                <h1 className="admin-login-h1">XTool</h1>
                <span className="admin-login-h1-sub">运维台</span>
              </div>
              <p className="admin-login-kicker">Management Infrastructure Access</p>
            </div>

            <Form<LoginForm>
              layout="vertical"
              requiredMark={false}
              onFinish={onFinish}
              className="admin-login-form"
            >
              <Form.Item
                name="email"
                label={<span className="admin-login-label">邮箱地址</span>}
                rules={[
                  { required: true, message: "请输入邮箱" },
                  { type: "email", message: "邮箱格式不正确" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="operator@xtool.internal"
                  autoComplete="email"
                  className="admin-login-input"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label={
                  <span className="admin-login-label-row">
                    <span className="admin-login-label">密码</span>
                  </span>
                }
                rules={[{ required: true, message: "请输入密码" }]}
              >
                <Input.Password
                  size="large"
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  className="admin-login-input"
                />
              </Form.Item>

              <div className="admin-login-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="admin-login-submit"
                >
                  <span className="admin-login-submit-inner">
                    登录
                    <ArrowRightOutlined />
                  </span>
                </Button>
                <div className="admin-login-links">
                  <span className="admin-login-link is-disabled">忘记密码</span>
                  <span className="admin-login-link is-disabled">联系管理员</span>
                </div>
              </div>
            </Form>

            <div className="admin-login-status-block">
              <div className="admin-login-status-row">
                <span
                  className={`admin-login-pulse ${apiDown ? "is-down" : ""}`}
                  aria-hidden
                />
                <span className="admin-login-status-text">
                  {apiDown
                    ? "API 不可用 · 请启动后端并确认代理指向 8010"
                    : `生产节点 · 延迟约 ${latencyMs}ms（/api/plans）`}
                </span>
              </div>
            </div>
          </div>
          <div className="admin-login-watermark" aria-hidden>
            XT
          </div>
        </div>
      </main>

      <footer className="admin-login-footer">
        <p className="admin-login-footer-copy">
          © 2024 XTool Operations. All activities are monitored and recorded for security
          auditing purposes.
        </p>
        <div className="admin-login-footer-links">
          <span className="admin-login-footer-a is-disabled">Security Policy</span>
          <span className="admin-login-footer-dot" />
          <span className="admin-login-footer-a is-disabled">System Status</span>
          <span className="admin-login-footer-dot" />
          <span className="admin-login-footer-a is-disabled">Contact Admin</span>
        </div>
      </footer>

      <div className="admin-login-bg-deco" aria-hidden />
    </div>
  );
}
